import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { translateText } from "../../services/fetch";
import { useClientAccessContext } from "../ClientAccessContext";

const TARGET_LANGUAGE = "English";

// Only documents that aren't already in the target language can be translated.
export const isTranslatable = (language?: string) =>
	!!language && language.trim().toLowerCase() !== TARGET_LANGUAGE.toLowerCase();

type TranslationStatus = "loading" | "done";

interface TranslationEntry {
	status: TranslationStatus;
	text?: string;
}

export interface ITranslationContext {
	isTranslateOn: (documentId: number) => boolean;
	toggleTranslate: (documentId: number) => void;
	requestTranslation: (documentId: number, sourceText: string) => void;
	getTranslation: (sourceText: string) => TranslationEntry | undefined;
}

const TranslationContext = createContext({} as ITranslationContext);

// The cache is keyed by the source string (+ target language) so that an
// identical string - most importantly the title, which is shared across all
// card views - is only ever translated once and reused everywhere.
const cacheKey = (sourceText: string) => `${TARGET_LANGUAGE}::${sourceText}`;

const TranslationProvider: React.FunctionComponent<{
	children?: React.ReactNode;
}> = ({ children }) => {
	const { selectedClient } = useClientAccessContext();

	const [translateOn, setTranslateOn] = useState<Record<number, boolean>>({});
	const [cache, setCache] = useState<Record<string, TranslationEntry>>({});
	// Tracks keys that already have a request in flight so the title + body
	// requests fired together never translate the same string twice.
	const inFlight = useRef<Set<string>>(new Set());

	const isTranslateOn = useCallback(
		(documentId: number) => !!translateOn[documentId],
		[translateOn],
	);

	const toggleTranslate = useCallback((documentId: number) => {
		setTranslateOn((prev) => ({ ...prev, [documentId]: !prev[documentId] }));
	}, []);

	const getTranslation = useCallback(
		(sourceText: string) => cache[cacheKey(sourceText)],
		[cache],
	);

	const requestTranslation = useCallback(
		(documentId: number, sourceText: string) => {
			const text = sourceText?.trim();
			if (!text || !selectedClient?.clientName) return;

			const key = cacheKey(sourceText);
			if (cache[key] || inFlight.current.has(key)) return;

			// On failure, drop the cache entry (so it can be retried) and turn the
			// document's toggle back off, so the button returns to its idle state
			// instead of looking "on".
			const handleFailure = () => {
				setCache((prev) => {
					const next = { ...prev };
					delete next[key];
					return next;
				});
				setTranslateOn((prev) => ({ ...prev, [documentId]: false }));
			};

			inFlight.current.add(key);
			setCache((prev) => ({ ...prev, [key]: { status: "loading" } }));

			translateText(documentId, selectedClient.clientName, sourceText)
				.then((response) => {
					if (response?.isSuccess) {
						setCache((prev) => ({
							...prev,
							[key]: { status: "done", text: response.result },
						}));
					} else {
						handleFailure();
					}
				})
				.catch(handleFailure)
				.finally(() => {
					inFlight.current.delete(key);
				});
		},
		[cache, selectedClient?.clientName],
	);

	return (
		<TranslationContext.Provider
			value={{
				isTranslateOn,
				toggleTranslate,
				requestTranslation,
				getTranslation,
			}}
		>
			{children}
		</TranslationContext.Provider>
	);
};

const useTranslationContext = () => {
	const context = useContext(TranslationContext);
	if (context === undefined) {
		throw new Error(
			"useTranslationContext must be used within a TranslationProvider",
		);
	}
	return context;
};

/**
 * Returns the translated version of `sourceText` when the document's translate
 * toggle is on, otherwise the original. Only ever requests the string passed in,
 * so translation stays strictly on demand per rendered text/view. While a
 * translation is loading the original text is shown so the card never blanks.
 */
export const useTranslatedText = (
	documentId: number,
	sourceText: string,
): { text: string; loading: boolean } => {
	const { isTranslateOn, getTranslation, requestTranslation } =
		useTranslationContext();
	const on = isTranslateOn(documentId);

	useEffect(() => {
		if (on && sourceText) requestTranslation(documentId, sourceText);
	}, [on, sourceText, documentId, requestTranslation]);

	if (!on || !sourceText) return { text: sourceText, loading: false };

	const entry = getTranslation(sourceText);
	if (!entry || entry.status === "loading")
		return { text: sourceText, loading: true };
	return { text: entry.text ?? sourceText, loading: false };
};

export { TranslationProvider, useTranslationContext };
